import React, { useState } from "react";
import {
  Grid,
  Typography,
  Tooltip,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  makeStyles,
  Theme,
} from "@material-ui/core";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import CloseIcon from "@material-ui/icons/Close";
import InsertDriveFileIcon from "@material-ui/icons/InsertDriveFile";
import { DropzoneArea } from "material-ui-dropzone";

const useStyles = makeStyles((theme: Theme) => ({
  attachButton: {
    backgroundColor: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: 10,
    textTransform: "none",
    color: "#64748b",
    fontSize: "0.875rem",
    fontWeight: 500,
    padding: "10px 16px",
    justifyContent: "center",
    boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.03)",
    transition: "all 0.2s ease-in-out",
    "&:hover": {
      backgroundColor: "#ffffff",
      borderColor: "#cbd5e1",
      color: "#334155",
      boxShadow: "0 2px 4px 0 rgba(0, 0, 0, 0.05)",
    },
  },
  attachIcon: {
    fontSize: 18,
    color: "#ffffff",
  },
  fileBadgeContainer: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  fileBadge: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    padding: "6px 12px",
    fontSize: "0.8125rem",
    color: "#334155",
  },
  fileNameContainer: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    overflow: "hidden",
  },
  fileName: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    fontWeight: 500,
  },
  fileIcon: {
    fontSize: 16,
    color: "#e08c00",
  },
  removeIconBtn: {
    padding: 2,
    color: "#94a3b8",
    "&:hover": {
      color: "#ef4444",
      backgroundColor: "#fef2f2",
    },
  },
}));

interface FileSectionProps {
  files: File[] | any[];
  setFiles: (files: any[]) => void;
  isNew?: boolean;
  noteId?: string | number;
}

export const FileSection = ({ files, setFiles }: FileSectionProps) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);

  const handleRemoveFile = (indexToRemove: number) => {
    setFiles(files.filter((_: any, index: number) => index !== indexToRemove));
  };

  return (
    <>
      <Tooltip title="Attach or upload files to note">
        <Button
          variant="contained"
          startIcon={<AttachFileIcon className={classes.attachIcon} />}
          style={{ backgroundColor: "#E08A12", color: "#fff", height: 29 }}
          onClick={() => setOpen(true)}
          fullWidth
          className={classes.attachButton}
          disableElevation
        >
          {files.length > 0
            ? `${files.length} File${files.length > 1 ? "s" : ""} Attached`
            : "Attach a file"}
        </Button>
      </Tooltip>

      {/* Selected file tags list */}
      {files.length > 0 && (
        <div className={classes.fileBadgeContainer}>
          {files.map((file: any, index: number) => (
            <div key={index} className={classes.fileBadge}>
              <div className={classes.fileNameContainer}>
                <InsertDriveFileIcon className={classes.fileIcon} />
                <span className={classes.fileName} title={file.name}>
                  {file.name || `File ${index + 1}`}
                </span>
              </div>
              <IconButton
                size="small"
                className={classes.removeIconBtn}
                onClick={() => handleRemoveFile(index)}
              >
                <CloseIcon style={{ fontSize: 14 }} />
              </IconButton>
            </div>
          ))}
        </div>
      )}

      {/* Upload Modal */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogContent>
          <Grid container direction="column" spacing={2}>
            <Grid item>
              <Typography variant="h6" style={{ fontWeight: 600 }}>
                Upload Attachments
              </Typography>
            </Grid>
            <Grid item>
              <DropzoneArea
                initialFiles={files}
                filesLimit={
                  process.env.REACT_APP_FILE_LIMIT
                    ? parseInt(process.env.REACT_APP_FILE_LIMIT)
                    : 20
                }
                maxFileSize={12000000}
                onChange={(newFiles) => setFiles(newFiles)}
                showPreviews={true}
                showPreviewsInDropzone={true}
                useChipsForPreview={true}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} color="primary">
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};